// w39 Tungi Saroy (Midnight Palace) — midnight-blue & gold wedding preview
// card.
//
// The full template is a deep-navy night invitation with a thin gold ring
// monogram, shimmer-gold serif name lines and scattered starlight. The
// catalog card mirrors that: a midnight-navy radial gradient ground, a thin
// gold ring holding the couple's initials, the couple's names in a
// shimmer-gold serif treatment, a gold rule + date line below, and a
// scatter of tiny gold star dots. Uses the site's already-loaded serif
// stack (via `font`, e.g. Marcellus from globals.css) — no new font import.
import { type LayoutCtx } from "./_shared";

// Deterministic star scatter — same positions on every render (no Math.random
// so SSR/CSR + repeated mounts stay visually stable).
const STARS: Array<{ x: number; y: number; s: number; o: number }> = [
  { x: 8, y: 10, s: 1.6, o: 0.9 },
  { x: 88, y: 14, s: 1.2, o: 0.7 },
  { x: 18, y: 22, s: 1, o: 0.6 },
  { x: 78, y: 24, s: 1.4, o: 0.8 },
  { x: 92, y: 34, s: 1, o: 0.55 },
  { x: 6, y: 40, s: 1.3, o: 0.7 },
  { x: 12, y: 58, s: 1, o: 0.5 },
  { x: 90, y: 55, s: 1.5, o: 0.85 },
  { x: 82, y: 70, s: 1, o: 0.55 },
  { x: 9, y: 74, s: 1.2, o: 0.65 },
  { x: 20, y: 88, s: 1, o: 0.5 },
  { x: 85, y: 90, s: 1.3, o: 0.7 },
];

// Split "Timur & Madina" / "Jasur va Gulnora" / "Дилшод и Мадина" into
// initials for the monogram ring. Falls back to a single initial when only
// one name is present — no dangling "&".
function initials(names: string): string {
  const parts = names
    .split(/\s*[&+—]\s*|\s+(?:va|и)\s+/i)
    .map((p) => p.trim())
    .filter(Boolean);
  const letters = parts.map((p) => p.charAt(0).toUpperCase());
  return letters.length > 1 ? `${letters[0]} & ${letters[1]}` : letters[0] || "";
}

export function MidnightPalacePreviewLayout({ names, date, accent, ink, bg, font }: LayoutCtx) {
  const gold = accent || "#d8b65c";
  const navy = bg || "#0b1026";
  const mono = initials(names);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `radial-gradient(120% 90% at 50% 8%, #1a2247 0%, ${navy} 55%, #05070f 100%)`,
        padding: "6%",
        display: "flex",
        overflow: "hidden",
      }}
    >
      {/* Scattered gold stars. */}
      {STARS.map((star, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: `${star.x}%`,
            top: `${star.y}%`,
            width: star.s,
            height: star.s,
            borderRadius: "50%",
            background: gold,
            opacity: star.o,
            boxShadow: `0 0 3px ${gold}`,
          }}
        />
      ))}

      <div
        style={{
          flex: 1,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${gold}66`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.38em",
            color: gold,
            marginTop: "4%",
            marginBottom: "8%",
          }}
        >
          TO'Y TAKLIFNOMASI
        </div>

        {/* Thin gold ring monogram. */}
        <div
          style={{
            width: "30%",
            aspectRatio: "1 / 1",
            borderRadius: "50%",
            marginBottom: "8%",
            display: "grid",
            placeItems: "center",
            boxShadow: `inset 0 0 0 1px ${gold}, inset 0 0 0 4px transparent, inset 0 0 0 5px ${gold}44`,
          }}
        >
          <span
            style={{
              fontFamily: font,
              fontSize: 13,
              letterSpacing: "0.06em",
              color: gold,
            }}
          >
            {mono}
          </span>
        </div>

        {/* Shimmer-gold names. */}
        <div
          style={{
            fontFamily: font,
            fontSize: 26,
            lineHeight: 1.15,
            letterSpacing: "0.01em",
            backgroundImage: `linear-gradient(90deg, ${gold}99 0%, #f3e3b0 45%, ${gold}99 100%)`,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Gold rule. */}
        <div style={{ width: "34%", height: 1, background: `${gold}88`, marginBottom: "6%" }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.3em",
            color: ink,
          }}
        >
          {date.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
